import {
  CalendarOptions,
  DateSelectArg,
  DatesSetArg,
  EventClickArg,
  EventSourceInput,
} from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { ResourceSourceInput } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { Box } from '@mui/material';
import FullCalendarContainer from 'components/scheduler/FullCalendarContainer';
import dayjs, { Dayjs } from 'dayjs';
import { ScheduleElement, ScheduleItemType } from 'remote/global';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { usePostOperatorsRidesMoveMutation } from 'api/queries/operators';
import { SchedulerTypeEnum, SelectedJoinEventsProps } from 'utils/data/data-types';
import {
  findClosestMatch,
  getCustomWeekViewRange,
  getMonthViewRange,
  hasWritePermission,
} from 'utils/helpers';
import { useDeferredPromise } from 'utils/hooks/useDeferredPromise';
import { useUISchedulerHoveredEvent } from 'api/queries/ui';
import {
  renderAvailabilityEventContent,
  renderAvailabilityResourceContent,
  renderAvailabilityResourceGroupLabel,
  renderAvailabilityResourceHeaderContent,
} from 'utils/component-utils';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FullCalendarToolbar from 'components/scheduler/FullCalendarToolbar';
import JoinDepoLegsModal from 'components/modals/JoinDepoLegsModal';
import useOperatorTimezone from '@/utils/hooks/useOperatorTimezone';

type Props = {
  resources?: ResourceSourceInput;
  events?: EventSourceInput;
  schedulerType: SchedulerTypeEnum;
  calendarRef: React.RefObject<FullCalendar>;
  calendarProps?: CalendarOptions;
  handleEventClick?: (e: EventClickArg) => void;
  handleDateChange?: (e: DatesSetArg) => void;
  handleSelectRange?: (e: DateSelectArg) => void;
};

type Interval = {
  start: Dayjs;
  end: Dayjs;
};

const isIntervalOverlapping = (interval1: Interval, interval2: Interval): boolean =>
  interval1.end.isAfter(interval2.start) && interval1.start.isBefore(interval2.end);

function CustomFullCalendar({
  calendarRef,
  resources,
  events,
  schedulerType,
  calendarProps,
  handleEventClick,
  handleDateChange,
  handleSelectRange,
}: Props) {
  const { t, i18n } = useTranslation();
  const [, setDummyState] = useState(0);
  const [isJoinDepoLegsModalOpen, setIsJoinDepoLegsModalOpen] = useState(false);
  const [selectedJoinEvents, setSelectedJoinEvents] = useState<SelectedJoinEventsProps>();
  const { defer, deferRef } = useDeferredPromise<boolean>();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions = {} } = userInfo ?? {};
  const { setSchedulerHoveredEvent } = useUISchedulerHoveredEvent();
  const postOperatorsRidesMoveMutation = usePostOperatorsRidesMoveMutation();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const operatorTimezone = useOperatorTimezone();

  const getIsEventEditable = () => {
    if (schedulerType !== SchedulerTypeEnum.BUS_SCHEDULER) return false;
    if (hasWritePermission(permissions?.ORDER)) {
      return true;
    }
    return false;
  };

  const handleCloseDepoDialog = () => {
    setIsJoinDepoLegsModalOpen(false);
    setSelectedJoinEvents(undefined);
    deferRef?.resolve(false);
  };

  const handleConfirmDepoDialog = () => {
    setIsJoinDepoLegsModalOpen(false);
    setSelectedJoinEvents(undefined);
    deferRef?.resolve(true);
  };

  const initialCalendarView = sessionStorage.getItem(schedulerType) ?? 'resourceTimelineDay';

  // used to fix issue with driver names overlapping
  const forceRerender = () => {
    setDummyState((prevState) => prevState + 1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      forceRerender();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box display='flex' flexDirection='column' width='100%' height='100%' position='relative'>
      <JoinDepoLegsModal
        open={isJoinDepoLegsModalOpen}
        event={selectedJoinEvents}
        setOpen={setIsJoinDepoLegsModalOpen}
        onClose={handleCloseDepoDialog}
        onDecline={handleCloseDepoDialog}
        onConfirm={handleConfirmDepoDialog}
      />
      <FullCalendarContainer>
        <FullCalendarToolbar schedulerType={schedulerType} calendarRef={calendarRef} />
        <FullCalendar
          locale={i18n.language.includes('en') ? 'en-gb' : i18n.language}
          firstDay={1}
          timeZone={operatorTimezone}
          ref={calendarRef}
          plugins={[resourceTimelinePlugin, interactionPlugin]}
          headerToolbar={false}
          initialView={initialCalendarView}
          buttonText={{ today: t('SchedulerView.attributes.today').toString() }}
          eventClick={handleEventClick}
          eventContent={(eventContent) =>
            renderAvailabilityEventContent(eventContent, i18n.language)
          }
          resourceLabelContent={renderAvailabilityResourceContent}
          resourceGroupLabelContent={(args) => renderAvailabilityResourceGroupLabel({ ...args, t })}
          resources={resources}
          events={events || []}
          slotLabelContent={renderAvailabilityResourceHeaderContent}
          scrollTimeReset={false}
          eventMaxStack={1}
          moreLinkText={(num) => t('SchedulerView.attributes.viewMore', { count: num })}
          moreLinkHint={(num) => t('SchedulerView.attributes.viewMoreHint', { count: num })}
          eventMouseEnter={(args) => {
            const rideType = args.event.extendedProps.itemType;
            if (rideType === ScheduleItemType.RIDE) setSchedulerHoveredEvent(args);
          }}
          eventMouseLeave={() => setSchedulerHoveredEvent(null)}
          datesSet={handleDateChange}
          select={handleSelectRange}
          slotMinWidth={6}
          eventMinWidth={3}
          views={{
            resourceTimelineDay: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.day'),
              duration: { days: 1 },
              slotDuration: { minutes: 5 },
              slotLabelInterval: { minutes: 60 },
            },
            resourceTimelineTwoDay: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.twoDays'),
              duration: { days: 2 },
              slotDuration: { minutes: 15 },
              slotLabelInterval: { minutes: 60 },
            },
            customWeekView: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.week'),
              visibleRange: getCustomWeekViewRange,
              duration: { weeks: 1 },
              slotDuration: { hours: 1 },
              slotLabelInterval: { hours: 24 / 8 },
            },
            resourceTimelineTwoWeek: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.twoWeeks'),
              duration: { weeks: 2 },
              slotDuration: { hours: 1 },
              slotLabelInterval: { hours: 24 / 4 },
            },
            customMonthView: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.month'),
              visibleRange: getMonthViewRange,
              dateIncrement: { month: 1 },
              slotDuration: { hours: 1 },
              slotLabelInterval: { hours: 24 / 4 },
            },
            pastAndFutureView: {
              type: 'resourceTimeline',
              buttonText: t('SchedulerView.attributes.customDates'),
              slotDuration: { hours: 1 },
            },
          }}
          height={smallScreen ? 'auto' : 'calc(100vh - 260px)'}
          editable={getIsEventEditable()}
          eventDragStop={() => {
            // Some cases makes hovered event persist, just hide it
            setSchedulerHoveredEvent(null);
          }}
          eventDrop={async (args) => {
            const { event, newResource } = args;
            const resourceId = newResource?.id;

            if (!resourceId) {
              args.revert();
              return;
            }

            if (Array.isArray(events)) {
              const rowEvents = events.filter((ev) => Number(ev.resourceId) === Number(resourceId));

              const closestMatch = findClosestMatch(rowEvents, new Date(event.start as Date));
              const isClosestMatchRideType = closestMatch?.itemType === ScheduleItemType.RIDE;

              if (closestMatch && isClosestMatchRideType) {
                setIsJoinDepoLegsModalOpen(true);
                setSelectedJoinEvents({
                  from: {
                    ...(event.extendedProps as ScheduleElement),
                    startTime: dayjs(event.start).toDate(),
                    endTime: dayjs(event.end).toDate(),
                  },
                  to: closestMatch as unknown as ScheduleElement,
                });
                // Wait for action on the JoinDepoLegsDialog
                const promise = await defer().promise;
                if (!promise) {
                  args.revert();
                  return;
                }
              }
            }

            // Ride id is only present when event is of type RIDE
            const rideId = event?.extendedProps.rideId;
            if (rideId == null) {
              args.revert();
              return;
            }

            try {
              await postOperatorsRidesMoveMutation.mutateAsync({
                rideId,
                vehicleId: resourceId,
              });
            } catch (err) {
              args.revert(); // Revert the positioning if request fails
            }
          }}
          eventAllow={(dropInfo, draggedEvent) => {
            const eventSeatsRange = draggedEvent?.extendedProps.seatsRange;
            const movable = draggedEvent?.extendedProps.movable;
            const resourceSeatsRange = dropInfo?.resource?.extendedProps?.seatsRange;
            const eventStartDate = draggedEvent?.startStr;
            const eventEndDate = draggedEvent?.endStr;
            const dropStartDate = dropInfo?.startStr;
            const dropEndDate = dropInfo?.endStr;
            const dropLicensePlate = dropInfo.resource?.extendedProps?.licensePlate;
            const dragLicensePlate = draggedEvent?.extendedProps?.licensePlate;

            if (!movable) return false;

            // Do not allow to move horizontally on the same axis
            if (dropLicensePlate === dragLicensePlate) return false;

            if (eventSeatsRange === resourceSeatsRange) {
              // Allow on same slot
              if (eventStartDate === dropStartDate && eventEndDate === dropEndDate) return true;
              return true;
            }

            return false;
          }}
          eventOverlap={(still, moving) => {
            const isRideType =
              still.extendedProps?.itemType === ScheduleItemType.RIDE &&
              moving?.extendedProps?.itemType === ScheduleItemType.RIDE;

            // If the events are not of type RIDE; just exit
            if (!isRideType) return false;

            const offset = dayjs().utcOffset();
            const stillStartTime = dayjs(still.start);
            const stillEndTime = dayjs(still.end);
            const stillClientStartTime = dayjs(still.extendedProps.clientStartTime).utcOffset(
              offset
            );
            const stillClientEndTime = dayjs(still.extendedProps.clientEndTime).utcOffset(offset);
            const movingStartTime = dayjs(moving?.start);
            const movingEndTime = dayjs(moving?.end);
            const movingClientStartTime = dayjs(moving.extendedProps.clientStartTime);
            const movingClientEndTime = dayjs(moving.extendedProps.clientEndTime);

            const isEventOverlapping = isIntervalOverlapping(
              { start: stillStartTime, end: stillEndTime },
              { start: movingStartTime, end: movingEndTime }
            );
            const isClientTimeOverlapping = isIntervalOverlapping(
              { start: stillClientStartTime, end: stillClientEndTime },
              { start: movingClientStartTime, end: movingClientEndTime }
            );

            if (isClientTimeOverlapping) return false;
            if (!isClientTimeOverlapping && isEventOverlapping) return true;
            if (!isEventOverlapping) return true;

            return false;
          }}
          resourceAreaWidth='150px'
          eventBackgroundColor='transparent'
          eventBorderColor='transparent'
          eventDurationEditable={false}
          selectable
          selectMirror
          dayMaxEvents
          weekends
          nowIndicator
          {...calendarProps}
        />
      </FullCalendarContainer>
    </Box>
  );
}
export default CustomFullCalendar;
